import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  IconButton,
  Tooltip,
  Tabs,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Alert,
  SelectChangeEvent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DifferenceIcon from '@mui/icons-material/Difference';
import ForumIcon from '@mui/icons-material/Forum';
import {
  Save as SaveIcon,
  ModelTraining,
  History as HistoryIcon,
  RestoreFromTrash as RestoreFromTrashIcon,
  Refresh as RefreshIcon,
  Work as WorkIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import PreviewIcon from '@mui/icons-material/Preview';
import EditDocumentIcon from '@mui/icons-material/EditDocument';

import { useReactToPrint } from 'react-to-print';

import { useAuth } from 'hooks/AuthContext';
import { Resume } from 'types/types';
import { BackstoryTextField } from 'components/BackstoryTextField';
import { JobInfo } from './JobInfo';
import { Scrollable } from 'components/Scrollable';
import * as Types from 'types/types';
import { StreamingOptions } from 'services/api-client';
import { StatusBox, StatusIcon } from './StatusIcon';
import { ResumeChat } from 'components/ui/ResumeChat';
import { DiffViewer } from 'components/DiffViewer';
import { ResumePreview, resumeStyles } from './ResumePreview';
import { useAppState } from 'hooks/GlobalContext';
import { useNavigate } from 'react-router-dom';
import { TabWithTooltip } from './TabWithTooltip';

interface ResumeRevision {
  revisionId: string;
  revisionTimestamp: string;
  updatedAt: string;
  createdAt: string;
  candidateId: string;
  jobId: string;
}

interface ResumeEditProps {
  onClose?: () => void;
  resumeId?: string;
  onSave?: (updatedResume: Resume) => void;
}

const ResumeEdit: React.FC<ResumeEditProps> = (props: ResumeEditProps) => {
  const { setSnack } = useAppState();
  const { onClose, resumeId, onSave } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isLarge = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { selectedCandidate, selectedJob } = useAppState();
  const { apiClient } = useAuth();
  const [editContent, setEditContent] = useState<string>('');

  const [saving, setSaving] = useState<boolean>(false);
  const [leftColumn, setLeftColumn] = useState('markdown');
  const [rightColumn, setRightColumn] = useState('chat');
  const [status, setStatus] = useState<string>('');
  const [statusType, setStatusType] = useState<Types.ApiActivityType | null>(null);
  const [error, setError] = useState<Types.ChatMessageError | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string>('corporate');
  const [resume, setResume] = useState<Resume | null>(null);
  const [isAIGenerated, setIsAIGenerated] = useState<boolean>(false);
  const [editPrompt, setEditPrompt] = useState<string>('');
  const [columnView, setColumnView] = useState<'left' | 'right'>('left');

  // Revision-related state
  const [revisions, setRevisions] = useState<ResumeRevision[]>([]);
  const [selectedRevision, setSelectedRevision] = useState<string>('current');
  const [loadingRevisions, setLoadingRevisions] = useState<boolean>(false);
  const [loadingRevision, setLoadingRevision] = useState<boolean>(false);
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const [current, setCurrent] = useState<Types.Resume | null>(null);
  const [backupContent, setBackupContent] = useState<string>('');

  const printContentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({
    contentRef: printContentRef,
    pageStyle: '@page { margin: 8mm !important; }',
  });

  useEffect(() => {
    setSaveDisabled(
      !resume ||
        statusType !== null ||
        saving ||
        editContent.trim() === '' ||
        selectedRevision !== 'current' ||
        (editContent === resume?.resume && isAIGenerated === false)
    );
  }, [resume, statusType, saving, editContent, isAIGenerated]);

  // Initialize edit content when dialog opens
  useEffect(() => {
    if (!selectedCandidate || !selectedJob) {
      setSnack('No candidate or job selected for resume generation.', 'error');
      return;
    }

    if (!resumeId && !resume) {
      apiClient
        .getResumesByCandidate(selectedCandidate.id || '', selectedJob.id || '')
        .then(resumes => {
          if (resumes.length > 0) {
            console.log(
              `Found ${resumes.length} resumes for candidate ${selectedCandidate.id} and job ${selectedJob.id}`
            );
            setResume(resumes[0]);
            setCurrent(resumes[0]);
            setBackupContent(resumes[0].resume);
          } else {
            console.log(
              `No resumes found for candidate ${selectedCandidate.id} and job ${selectedJob.id}. Generating new resume...`
            );
            generateResume();
          }
        })
        .catch(error => {
          console.log(error);
        });
      return;
    }

    if (!resume) {
      apiClient.getResume(resumeId || '').then(loadedResume => {
        console.log(`Loading resume with ID: ${resumeId}`);
        setResume(loadedResume);
        setCurrent(loadedResume);
        setBackupContent(loadedResume.resume);
      });
      return;
    }

    console.log(`Resume was ${resume.aiGenerated ? 'AI-generated' : 'user-generated'}`, resume);
    setEditPrompt(resume.prompt || '');
    setIsAIGenerated(resume.aiGenerated || false);
    setEditContent(resume.resume);
    loadResumeRevisions();
  }, [resume]);

  const currentStyle = useMemo(() => {
    return resumeStyles[selectedStyle];
  }, [selectedStyle]);

  // Load resume revisions
  const loadResumeRevisions = async (): Promise<void> => {
    if (!resume) return;

    setLoadingRevisions(true);
    try {
      const response = await apiClient.getResumeRevisions(resume.id || '');
      console.log('Loaded revisions:', response.revisions);
      setRevisions(response.revisions || []);
    } catch (error) {
      if (error instanceof Error) {
        console.error('Failed to load revisions:', error);
        setSnack(`Failed to load resume revisions: ${error.message || 'Unknown error'}`, 'error');
      }
    } finally {
      setLoadingRevisions(false);
    }
  };

  // Load specific revision content
  const loadRevision = async (revisionId: string): Promise<void> => {
    if (!resume || revisionId === 'current') {
      return;
    }

    setLoadingRevision(true);
    try {
      const response = await apiClient.getResumeRevision(resume.id || '', revisionId);
      setResume(response.revision);
      setEditPrompt(response.revision.prompt || '');
      setIsAIGenerated(response.revision.aiGenerated || false);
    } catch (error) {
      console.error('Failed to load revision:', error);
      setSnack('Failed to load revision content.', 'error');
    } finally {
      setLoadingRevision(false);
    }
  };

  // Handle revision selection
  const handleRevisionChange = async (event: SelectChangeEvent<string>): Promise<void> => {
    const newRevisionId = event.target.value;
    if (selectedRevision === 'current') {
      setEditPrompt(current?.prompt || '');
      setIsAIGenerated(current?.aiGenerated || false);
      setSelectedRevision(newRevisionId);
      setBackupContent(editContent);
    } else {
      setSelectedRevision(newRevisionId);
      if (newRevisionId === 'current') {
        setEditContent(backupContent);
      }
      await loadRevision(newRevisionId);
    }
  };

  // Restore revision to editor
  const restoreRevision = (): void => {
    if (!resume) {
      setSnack('No revision selected to restore.', 'error');
      return;
    }
    setResume(resume);
    setEditContent(resume.resume);
    setSelectedRevision('current');
    setSnack('Revision restored to editor.', 'warning');
  };

  // Format timestamp for display
  const formatRevisionTimestamp = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return timestamp;
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!resume) {
      setSnack('No resume to save.', 'error');
      return;
    }
    setSaving(true);
    try {
      const resumeUpdate = {
        ...resume,
        resume: editContent,
        prompt: isAIGenerated ? editPrompt : 'Manual edits',
      };
      const result = await apiClient.updateResume(resumeUpdate);
      console.log('Resume updated:', result);
      const updatedResume = {
        ...resume,
        ...result,
      };
      setResume(updatedResume);
      setCurrent(updatedResume);
      setBackupContent(updatedResume.resume);
      onSave && onSave(updatedResume);
      setSnack('Resume updated successfully.');
      // Reload revisions to include the new version
      await loadResumeRevisions();
    } catch (error) {
      setSnack('Failed to update resume.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const generateResumeHandlers: StreamingOptions<Types.ChatMessageResume> = {
    onMessage: (_message: Types.ChatMessageResume) => {
      setStatus('');
      setStatusType(null);
      setSnack('Resume generation completed successfully.');
    },
    onStreaming: (chunk: Types.ChatMessageStreaming) => {
      // Close out any in-progress "thinking" status now that the model
      // has moved on to producing its actual response.
      setStatus('Generating resume...');
      setStatusType('generating');
      setEditContent(chunk.content);
    },
    onStatus: (status: Types.ChatMessageStatus) => {
      console.log('status:', status.content);
      setStatusType(status.activity);
      setStatus(status.content);
    },
    onError: (error: Types.ChatMessageError) => {
      console.log('error:', error);
      setStatusType(null);
      setStatus(error.content);
      setError(error);
    },
  };

  const generateResume = async (): Promise<Resume> => {
    if (!selectedCandidate || !selectedJob) {
      setSnack('No candidate or job selected for resume generation.', 'error');
      return Promise.reject('No candidate or job selected');
    }
    setEditContent('');
    setStatusType('thinking');
    setStatus('Starting resume generation...');
    setSelectedRevision('current');
    const request = apiClient.generateResume(
      selectedCandidate.id || '',
      selectedJob.id || '',
      generateResumeHandlers
    );
    const generatedResume = (await request.promise).resume;
    setResume(generatedResume);
    setCurrent(generatedResume);
    setBackupContent(generatedResume.resume);
    setSelectedRevision('current');
    setEditPrompt(generatedResume.prompt || '');
    setIsAIGenerated(true);
    await loadResumeRevisions();
    return generatedResume;
  };

  const handleLeftColumnChange = (event: React.SyntheticEvent, newValue: string): void => {
    setColumnView('left');
    if (newValue === 'print') {
      console.log('Printing resume...');
      reactToPrintFn();
      return;
    }
    if (newValue === 'regenerate') {
      setSnack('Regenerating resume...');
      setLeftColumn('markdown');
      generateResume();
      return;
    }
    if (newValue === 'undo') {
      setEditContent(current?.resume || '');
      setEditPrompt(current?.prompt || '');
      setIsAIGenerated(current?.aiGenerated || false);
      setLeftColumn('markdown');
      return;
    }
    setLeftColumn(newValue);
  };

  const handleRightColumnChange = (event: React.SyntheticEvent, newValue: string): void => {
    setColumnView('right');
    setRightColumn(newValue);
  };

  const handleClose = (): void => {
    onClose && onClose();
  };

  const changeLog =
    (selectedRevision !== 'current' && resume?.aiGenerated ? resume?.prompt : '') ||
    (isAIGenerated ? editPrompt : '') ||
    'Manual edits';

  return (
    <Box
      className="ResumeEdit"
      sx={{
        display: 'flex',
        flexGrow: 1,
        width: '100%',
        height: '100%',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <Box
        className="ResumeEditHeader"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          p: 2,
          position: 'relative',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                m: 0,
                p: 0,
                minWidth: '20rem',
              }}
            >
              {resume && (
                <>
                  <Typography variant="caption" display="block" color="text.secondary">
                    Resume for {resume.candidate?.fullName || resume.candidateId},{' '}
                    {resume.job?.title || 'No Job Title Assigned'},{' '}
                    {resume.job?.company || 'No Company Assigned'}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{ display: 'flex', flexDirection: 'row' }}
                  >
                    Resume ID:{' '}
                    <Box
                      sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => {
                        navigate(`/chat/${resume.id}`);
                      }}
                    >
                      {resume.id}
                    </Box>
                  </Typography>
                </>
              )}
              {!resume && (
                <Typography variant="caption" display="block" color="text.secondary">
                  No resume loaded. Please wait for generation to complete.
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
              {/* Style Selector */}
              <FormControl size="small" sx={{ minWidth: 'min-content' }}>
                <InputLabel id="resume-style-label">Resume Style</InputLabel>
                <Select
                  labelId="resume-style-label"
                  value={selectedStyle}
                  onChange={e => setSelectedStyle(e.target.value)}
                  label="Resume Style"
                >
                  {Object.entries(resumeStyles).map(([key, style]) => (
                    <MenuItem key={key} value={key}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          {style.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {style.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="revision-select-label">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HistoryIcon fontSize="small" />
                      Version History
                    </Box>
                  </InputLabel>
                  <Select
                    labelId="revision-select-label"
                    value={selectedRevision}
                    onChange={handleRevisionChange}
                    label="Version History"
                    disabled={loadingRevisions}
                  >
                    <MenuItem value="current">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="CURRENT" size="small" color="primary" variant="outlined" />
                        <Typography variant="body2">Current Version</Typography>
                      </Box>
                    </MenuItem>
                    {revisions.map(revision => (
                      <MenuItem key={revision.revisionId} value={revision.revisionId}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                          }}
                        >
                          <Typography variant="body2">
                            {formatRevisionTimestamp(revision.revisionTimestamp)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Updated: {formatRevisionTimestamp(revision.updatedAt)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.65rem' }}
                          >
                            ID: {revision.revisionId.substring(0, 8)}...
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Tooltip title="Refresh Revisions">
                  <IconButton
                    size="small"
                    onClick={loadResumeRevisions}
                    disabled={loadingRevisions}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>

                {selectedRevision !== 'current' && (
                  <>
                    <Tooltip title="Restore this revision to editor">
                      <Button
                        size="small"
                        startIcon={<RestoreFromTrashIcon />}
                        onClick={restoreRevision}
                        disabled={loadingRevision}
                      >
                        Restore
                      </Button>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        className="ResumeEditContent"
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          height: '100%',
        }}
      >
        <Box className="ResumeEditTabs" sx={{ display: 'flex', m: 0, p: 0 }}>
          <Tabs
            value={isLarge ? leftColumn : columnView === 'left' ? leftColumn : undefined}
            onChange={handleLeftColumnChange}
            sx={{
              '& .MuiTab-root': {
                minWidth: 'fit-content',
                width: 'fit-content',
                fontSize: isLarge ? '0.6rem' : '0.5rem',
              },
            }}
          >
            <TabWithTooltip
              tooltip="Markdown Editor"
              value="markdown"
              icon={<EditDocumentIcon />}
              label={isLarge ? 'Markdown' : ''}
            />
            <TabWithTooltip
              tooltip="View Changes"
              value="diff"
              disabled={editContent === current?.resume}
              icon={<DifferenceIcon />}
              label={isLarge ? 'Changes' : undefined}
            />
            <TabWithTooltip
              tooltip="Preview Resume"
              value="preview"
              icon={<PreviewIcon />}
              label={isLarge ? 'Preview' : undefined}
            />
            <TabWithTooltip
              tooltip="Print Resume"
              disabled={leftColumn !== 'preview'}
              value="print"
              icon={<PrintIcon />}
              label={isLarge ? 'Print' : undefined}
            />
            <TabWithTooltip
              tooltip="Regenerate Resume"
              value="regenerate"
              icon={<ModelTraining />}
              label={isLarge ? 'Regenerate' : undefined}
            />
            <TabWithTooltip
              tooltip="Undo Changes"
              value="undo"
              disabled={editContent === resume?.resume}
              icon={<UndoIcon />}
              label={isLarge ? 'Revert' : undefined}
            />
          </Tabs>
          <Tabs
            value={isLarge ? rightColumn : columnView === 'right' ? rightColumn : undefined}
            onChange={handleRightColumnChange}
            sx={{
              '& .MuiTab-root': {
                minWidth: 'fit-content',
                width: 'fit-content',
                fontSize: isLarge ? '0.6rem' : '0.5rem',
              },
              borderLeft: isLarge ? '1px solid #ccc' : 'none',
            }}
          >
            {resume && resume.job !== undefined && (
              <TabWithTooltip
                tooltip="Job"
                value="job"
                icon={<WorkIcon />}
                label={isLarge ? 'Job' : ''}
              />
            )}
            <TabWithTooltip
              tooltip="AI Edit"
              value="chat"
              icon={<ForumIcon />}
              label={isLarge ? 'AI Edit' : ''}
            />
          </Tabs>
        </Box>

        {status && (
          <Box sx={{ mt: 1, mb: 1, width: '100%' }}>
            <StatusBox>
              {statusType && <StatusIcon type={statusType} />}
              <Typography variant="body2" sx={{ ml: 1 }}>
                {status || 'Processing...'}
              </Typography>
            </StatusBox>
            {status && !error && <LinearProgress sx={{ mt: 1 }} />}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            height: '100%',
            gap: 1,
            pt: 1,
            width: '100%',
            position: 'relative',
            overflow: 'auto',
          }}
        >
          {(isLarge || columnView === 'left') && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                maxWidth: '100%',
                height: '100%',
                overflow: 'auto',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexGrow: 1,
                  width: '100%',
                  position: 'relative',
                  flexDirection: 'column',
                  m: 0,
                  p: 0,
                  gap: 0,
                }}
              >
                {selectedRevision !== 'current' && (
                  <Alert severity="info" sx={{ width: '100%' }}>
                    You are viewing a previous version. Click &quot;Restore&quot; to load this
                    content into the editor.
                  </Alert>
                )}

                {isAIGenerated && (
                  <Alert severity="warning" sx={{ width: '100%' }}>
                    This resume was generated by AI and has not been manually edited. Review and
                    then selecte &apos;Save&apos;.
                  </Alert>
                )}
              </Box>

              <Scrollable
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  width: '100%',
                  minHeight: 0,
                  '& > *:not(.Scrollable)': {
                    flexShrink: 0,
                  },
                  position: 'relative',
                }}
              >
                {leftColumn === 'markdown' && (
                  <>
                    {selectedRevision === 'current' ? (
                      <BackstoryTextField
                        value={editContent}
                        onChange={(value): void => setEditContent(value)}
                        style={{
                          position: 'relative',
                          maxHeight: '100%',
                          height: '100%',
                          width: '100%',
                          display: 'flex',
                          minHeight: '100%',
                          flexGrow: 1,
                          flex: 1,
                          overflowY: 'auto',
                          fontFamily: 'monospace',
                          backgroundColor: '#fafafa',
                          fontSize: '12px',
                        }}
                        placeholder="Enter resume content..."
                      />
                    ) : (
                      <Box
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          backgroundColor: 'grey.50',
                          height: '100%',
                          overflow: 'auto',
                        }}
                      >
                        {loadingRevision ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress sx={{ flexGrow: 1 }} />
                            <Typography variant="body2">Loading revision...</Typography>
                          </Box>
                        ) : (
                          <pre style={{ border: 0 }}>{resume?.resume}</pre>
                        )}
                      </Box>
                    )}
                  </>
                )}
                {leftColumn === 'diff' && current && (
                  <DiffViewer
                    changeLog={changeLog}
                    original={{ content: current.resume || '', name: 'original' }}
                    modified={{
                      content:
                        selectedRevision !== 'current' && resume ? resume.resume : editContent,
                      name: 'modified',
                    }}
                  />
                )}
                {leftColumn === 'preview' && resume && resume.candidate && (
                  <Box
                    className="document-container"
                    ref={printContentRef}
                    sx={currentStyle.contentStyle}
                  >
                    <ResumePreview resume={resume} selectedStyle={selectedStyle} />
                  </Box>
                )}
              </Scrollable>
              {resume && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    alignItems: 'center',
                    p: 1,
                  }}
                >
                  {onClose && <Button onClick={handleClose}>Cancel</Button>}
                  <Button
                    onClick={() => {
                      handleSave();
                    }}
                    variant="contained"
                    disabled={saveDisabled}
                    startIcon={<SaveIcon />}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                  <Typography variant="caption" display="block" color="text.secondary">
                    Last saved:{' '}
                    {resume.updatedAt ? new Date(resume.updatedAt).toLocaleString() : 'N/A'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          {(isLarge || columnView === 'right') && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                position: 'relative',
                width: '100%',
              }}
            >
              <Scrollable
                sx={{
                  flex: 1,
                  display: 'flex',
                  height: '100%',
                  width: '100%',
                  overflowY: 'auto',
                  position: 'relative',
                  p: 1,
                  flexDirection: 'column',
                }}
              >
                {resume && rightColumn === 'job' && (
                  <>
                    {resume.job !== undefined ? (
                      <JobInfo variant={'all'} job={resume.job} />
                    ) : (
                      <Box>No matching job found.</Box>
                    )}
                  </>
                )}
                {rightColumn === 'chat' && resume && (
                  <ResumeChat
                    session={resume.id || ''}
                    resume={editContent}
                    onResumeChange={(prompt: string, newResume: string): void => {
                      console.log('onResumeChange:', prompt);
                      if (newResume !== editContent) {
                        setBackupContent(editContent);
                        setEditPrompt(prompt);
                        setEditContent(newResume);
                        setIsAIGenerated(true);
                      }
                    }}
                    sx={{
                      m: 1,
                      p: 1,
                      flexGrow: 1,
                      position: 'relative',
                    }}
                  />
                )}
              </Scrollable>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export { ResumeEdit };
